import { setItem, getItem } from '@/common/storage';

const APPID = "wx601c8e002a90fdec"; // 公众号的appId

/**
 * 是否在配置中启用微信网页授权（.env 中 VUE_APP_WECHAT_AUTH_REQUIRED）
 * - 未配置或 true/1/yes：与历史行为一致，需要时在 401 后跳转 OAuth
 * - false/0/no：不跳转授权页（调试或非微信场景）
 */
export function isWechatAuthRequired() {
  const v = String(process.env.VUE_APP_WECHAT_AUTH_REQUIRED ?? 'true').toLowerCase();
  return v !== 'false' && v !== '0' && v !== 'no';
}

/**
 * 判断是否在微信环境中
 * 兼容鸿蒙系统 (HarmonyOS) 的微信
 */
export function isWechat() {
  const ua = navigator.userAgent.toLowerCase();
  // 检测微信环境，包括鸿蒙系统的微信
  // 鸿蒙微信可能包含 'hmos' 或 'harmonyos' 标识
  return ua.indexOf('micromessenger') !== -1;
}

/**
 * 判断是否为鸿蒙系统
 */
export function isHarmonyOS() {
  const ua = navigator.userAgent.toLowerCase();
  return ua.indexOf('hmos') !== -1 || 
         ua.indexOf('harmonyos') !== -1 ||
         ua.indexOf('huaweibrowser') !== -1;
}

/**
 * 判断是否是本地开发环境
 */
export function isLocalhost() {
  const hostname = window.location.hostname;
  return hostname === 'localhost' || 
         hostname === '127.0.0.1' || 
         hostname === '0.0.0.0' ||
         hostname.startsWith('192.168.') ||
         hostname.startsWith('10.') ||
         hostname.startsWith('172.');
}

/**
 * 获取URL参数
 * 改进版本，更好地处理各种URL格式
 */
export function getUrlParams() {
  const params = {};
  
  try {
    // 优先使用 URLSearchParams，更可靠
    const searchParams = new URLSearchParams(window.location.search);
    searchParams.forEach((value, key) => {
      params[key] = value;
    });
    
    // 如果没有从 search 获取到，尝试从 href 解析（兼容某些浏览器）
    if (Object.keys(params).length === 0) {
      const url = window.location.href;
      // 处理 URL 中可能存在的 hash
      const urlWithoutHash = url.split('#')[0];
      const queryString = urlWithoutHash.split('?')[1];
      
      if (queryString) {
        const pairs = queryString.split('&');
        pairs.forEach(pair => {
          const equalIndex = pair.indexOf('=');
          if (equalIndex > 0) {
            const key = pair.substring(0, equalIndex);
            const value = pair.substring(equalIndex + 1);
            try {
              params[decodeURIComponent(key)] = decodeURIComponent(value);
            } catch (e) {
              params[key] = value;
            }
          }
        });
      }
    }
  } catch (e) {
    console.error('解析URL参数失败:', e);
  }
  
  return params;
}

/** 无 id 参数时的默认活动ID */
const DEFAULT_ACTIVITY_ID = '26';

/**
 * 获取活动ID（支持 ?id=XX 多链接）
 * - URL 带 id/aid：使用并保存，其他页面通过路由 query 传递
 * - URL 无 id：不使用缓存，直接返回默认 26
 */
export function getActivityId() {
  const params = getUrlParams();
  const path = window.location.pathname || '';
  const isDetailsPage = path.includes('voting-details');
  const activityId = isDetailsPage ? params.aid : (params.id || params.aid);
  if (activityId) {
    setItem('id', activityId);
    return activityId;
  }
  return DEFAULT_ACTIVITY_ID;
}

/**
 * 获取code（从localStorage或URL参数）
 */
export function getCode() {
  try {
    // 优先从URL参数获取（授权回调时）
    const params = getUrlParams();
    if (params.code) {
      setItem('code', params.code);
      return params.code;
    }
    // 从localStorage获取
    return getItem('code');
  } catch (e) {
    console.error('获取code失败:', e);
    return null;
  }
}

/**
 * 获取接口鉴权参数，只传 code
 */
export function getAuthParams() {
  const code = getCode();
  if (code) return { code };
  return {};
}

/** 是否需等待微信 OAuth（生产微信内） */
export function shouldWaitForWechatAuth() {
  return isWechatAuthRequired() && isWechat() && !isLocalhost();
}

/** 授权跳转中的请求错误，此时不应弹业务弹窗 */
export function isAuthPendingError(error) {
  if (!error) return false;
  const msg = typeof error === 'string' ? error : (error.message || String(error));
  return msg === '需要授权' || msg === '等待授权' || msg === '会话失效';
}

/** show 接口是否表明会话已建立（有活动数据） */
export function isActivitySessionReady(res) {
  if (!res || Number(res.status) === 401) return false;
  return !!(res.giftvote && Object.keys(res.giftvote).length);
}

export function wechatOAuthDoneKey(activityId) {
  return `wechat_oauth_done_${activityId || getActivityId()}`;
}

/** show 带 code 授权成功后标记，回访时无 code 也可弹绑定框 */
export function markWechatOAuthDone(activityId) {
  setItem(wechatOAuthDoneKey(activityId), '1');
}

export function isWechatOAuthDone(activityId) {
  return getItem(wechatOAuthDoneKey(activityId)) === '1';
}

/**
 * 是否允许弹出绑定姓名框
 * 微信内须先完成 OAuth（URL 有 code，或本地曾授权成功）
 */
export function canPromptNameBind(res, usedCode) {
  if (!res || Number(res.status) === 401) return false;
  if (!shouldWaitForWechatAuth()) return true;
  if (usedCode || getUrlParams().code) return true;
  return isWechatOAuthDone();
}

/** 微信内未完成 OAuth 时立即跳转授权页 */
export function ensureWechatOAuthIfNeeded() {
  if (shouldWaitForWechatAuth() && !getCode() && !getUrlParams().code && !isWechatOAuthDone()) {
    jumpToWechatAuth();
    return true;
  }
  return false;
}

export function isIndexPage() {
  return (window.location.pathname || '').includes('/index');
}

/** 绑定姓名后清除一次性 code，后续走 cookie 会话 */
export function clearWechatAuthCode() {
  setItem('code', '');
  try {
    const url = new URL(window.location.href);
    url.searchParams.delete('code');
    url.searchParams.delete('state');
    const query = url.searchParams.toString();
    const nextUrl = url.pathname + (query ? `?${query}` : '') + (url.hash || '');
    window.history.replaceState(null, '', nextUrl);
  } catch (e) {
    // ignore
  }
}

/**
 * 获取当前页面的完整URL（用于授权回调）
 * 增强兼容性，特别是对鸿蒙系统的支持
 */
export function getCurrentUrl() {
  // 移除code和state参数，避免重复授权
  let url = window.location.href;
  
  try {
    // 处理可能存在的 hash 部分
    const hashIndex = url.indexOf('#');
    let hash = '';
    if (hashIndex !== -1) {
      hash = url.substring(hashIndex);
      url = url.substring(0, hashIndex);
    }
    
    // 尝试使用URL对象（适用于完整URL）
    const urlObj = new URL(url);
    urlObj.searchParams.delete('code');
    urlObj.searchParams.delete('state');
    urlObj.searchParams.delete('openid');
    
    return urlObj.toString() + hash;
  } catch (e) {
    console.warn('URL对象创建失败，使用备用方法:', e);
    // 如果URL对象创建失败，使用字符串处理
    const queryIndex = url.indexOf('?');
    if (queryIndex === -1) {
      return url;
    }
    
    const baseUrl = url.substring(0, queryIndex);
    const queryString = url.substring(queryIndex + 1);
    
    try {
      const params = new URLSearchParams(queryString);
      params.delete('code');
      params.delete('state');
      params.delete('openid');
      
      const newQueryString = params.toString();
      return newQueryString ? `${baseUrl}?${newQueryString}` : baseUrl;
    } catch (e2) {
      // 最后的备用方案：手动处理
      const pairs = queryString.split('&').filter(pair => {
        const key = pair.split('=')[0];
        return key !== 'code' && key !== 'state' && key !== 'openid';
      });
      const newQueryString = pairs.join('&');
      return newQueryString ? `${baseUrl}?${newQueryString}` : baseUrl;
    }
  }
}

const JUMP_THROTTLE_MS = 2000; // 2 秒内不重复跳，防止多请求同时 401 导致安卓机循环
let lastJumpTime = 0;

/**
 * 跳转到微信授权页面
 */
export function jumpToWechatAuth() {
  if (!isWechatAuthRequired()) return;
  if (!isWechat()) return;
  const now = Date.now();
  if (now - lastJumpTime < JUMP_THROTTLE_MS) return;
  lastJumpTime = now;

  const redirectUri = encodeURIComponent(getCurrentUrl());
  const authUrl = `https://open.weixin.qq.com/connect/oauth2/authorize?appid=${APPID}&redirect_uri=${redirectUri}&response_type=code&scope=snsapi_base&state=STATE#wechat_redirect`;
  window.location.href = authUrl;
}


/**
 * 授权逻辑已简化：不做前置检查，由 ajax 拦截器在后端 401 时跳转微信授权
 * 以下保留供 getAuthParams 等使用
 */
